import styled from "@emotion/styled";
import { type ReactNode, useContext, useState } from "react";
import { useCopyToClipboard, useKey, useLocalStorage } from "react-use";
import { type BaseEditor, Editor, Node, Path, Transforms } from "slate";

import Button, { type ButtonProps } from "@imminently/decisively-ui/Button";
import Icon from "@imminently/decisively-ui/Icon";
import Tooltip from "@imminently/decisively-ui/Tooltip";
import {
  ELEMENT_H1,
  ELEMENT_H2,
  ELEMENT_H3,
  ELEMENT_H4,
  ELEMENT_H5,
  ELEMENT_H6,
  ELEMENT_HR,
  ELEMENT_LIC,
  ELEMENT_OL,
  ELEMENT_PARAGRAPH,
  ELEMENT_TABLE,
  ELEMENT_UL,
  LinkToolbarButton,
  MARK_BOLD,
  MARK_ITALIC,
  MARK_STRIKETHROUGH,
  MARK_UNDERLINE,
  type PlateEditor,
  type TElement,
  findNodePath,
  focusEditor,
  getNode,
  getNodeTexts,
  getParentNode,
  insertNodes,
  isElement,
  removeNodes,
  replaceNodeChildren,
  setNodes,
  someNode,
  toggleList,
  toggleNodeType,
  usePlateEditorRef,
  usePlateEditorState,
  withoutNormalizing,
} from "@udecode/plate";

import ButtonGroup from "@imminently/decisively-ui/ButtonGroup";
import DropdownMenu from "@imminently/decisively-ui/DropdownMenu";
import {
  ELEMENT_BANNER,
  ELEMENT_CONCLUSION,
  ELEMENT_CONDITION,
  ELEMENT_OPERATOR,
  ELEMENT_RULE,
  ELEMENT_SECTION,
  type SectionData,
  createSection,
  createSectionFromNodes,
  getRuleLevel,
  insertOperator,
  paragraphToRule,
  ruleToParagraph,
} from "../../components";
import { isLastTableRow, isRule, isTableHeaderRow } from "../../components/rule/ruleTransforms";
import { BlockToolbarButton, EditorToolbarButton, MarkToolbarButton, useToolbarListState } from "../../plugins/toolbar";
import { createParagraph, format, getSelectedNode } from "../../plugins/util";
import { EditorState, useOptionalMultiEditor } from "../multiEditor";

import { useDocumentEditors } from "@/app/providers/services";
import { ELEMENT_CONNECTOR, insertConnector } from "@/common/editor/components/operator/connector";
import { type SuggestSection, useOpenSuggestRulesModal } from "@/common/editor/components/suggest/SuggestRulesModal";
import { useAutoSectionModal } from "@/common/editor/components/suggest/useAutoSectionModal";
import { useCurrentRelease } from "@/common/hooks";
import { useNotification } from "@/common/notifications";
import { Avatar, AvatarGroup } from "@/components";
import { FeatureGuard } from "@/components/FeatureGuard";
import HighlightConnectedToRootModal from "@/components/highlightConnected/HighlightConnectedToRootModal";
import { cn } from "@/lib/utils";
import { RuleDocumentContext, useRuleDocument, useSections } from "@/pages/documents";
import { useDocumentMetadataModal } from "@/pages/documents/DocumentMetadataModal";
import { useRelease } from "@/pages/models/release/ReleaseContextProvider";
import Toolbar from "@imminently/decisively-ui/Toolbar";
import { capitalize } from "lodash";
import { useTranslation } from "react-i18next";
import { Banner, getBannerColour } from "../../components/banner";
import { TablesToolbar } from "../../components/decisionTable";
import { insertRuleBreak } from "../../components/ruleBreak";
import { useDeferredEditorFocus } from "./useDeferredEditorFocus";

const Heading = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
`;

const useElementLabel = (element: TElement) => {
  const { t } = useTranslation();
  switch (element.type) {
    case ELEMENT_PARAGRAPH:
    case ELEMENT_LIC: // list elements are also paragraphs
      return t("documents.paragraph_label");
    case ELEMENT_H1:
      return t("documents.heading_1_label");
    case ELEMENT_H2:
      return t("documents.heading_2_label");
    case ELEMENT_H3:
      return t("documents.heading_3_label");
    case ELEMENT_H4:
      return t("documents.heading_4_label");
    case ELEMENT_H5:
      return t("documents.heading_5_label");
    case ELEMENT_H6:
      return t("documents.heading_6_label");
    case ELEMENT_OL:
      return t("documents.numbered_list_tooltip");
    case ELEMENT_UL:
      return t("documents.bullet_tooltip");
    case ELEMENT_CONCLUSION:
      return t("documents.conclusion_label");
    case ELEMENT_CONDITION:
      return t("documents.condition_label");
    case ELEMENT_SECTION:
      return t("documents.section_tooltip");
    case ELEMENT_TABLE:
      return t("documents.decision_table_label");
    case ELEMENT_BANNER:
      return t(`documents.${element.banner}_banner_label`);
    case ELEMENT_RULE:
      return t(`documents.${element.expression}_label`);
    default:
      return t("documents.unknown_label");
  }
};

const getElementIcon = (element: TElement) => {
  switch (element.type) {
    case ELEMENT_PARAGRAPH:
      return <Icon name="non-rule" />;
    case ELEMENT_H1:
      return <Icon name="level-1" />;
    case ELEMENT_H2:
      return <Icon name="level-2" />;
    case ELEMENT_H3:
      return <Icon name="level-3" />;
    case ELEMENT_H4:
      return <Icon name="level-4" />;
    case ELEMENT_H5:
      return <Icon name="level-5" />;
    case ELEMENT_H6:
      return <Icon name="level-6" />;
    case ELEMENT_OL:
      return <Icon name="list-ordered" />;
    case ELEMENT_UL:
      return <Icon name="list" />;
    case ELEMENT_CONCLUSION:
      return <Icon name="outcome" />;
    case ELEMENT_CONDITION:
      return <Icon name="add-rule" />;
    case ELEMENT_SECTION:
      return <Icon name="add-group" />;
    case ELEMENT_TABLE:
      return <Icon name="add-table" />;
    case ELEMENT_BANNER:
      return <Icon name="bookmark" />;
    case ELEMENT_RULE:
      return <Icon name="outcome" />;
    default:
      return <>🤷</>;
  }
};

const AvatarPopperProps = { modifiers: { offset: { offset: "0, -10" }, flip: { enabled: false } } };

const MenuIconItem = ({ icon, name, ...props }: { icon: ReactNode; name: string } & Record<string, any>) => (
  <Heading {...props}>
    <DropdownMenu.Item.Icon>{icon}</DropdownMenu.Item.Icon>
    <DropdownMenu.Item.Label className="font-normal">{capitalize(name)}</DropdownMenu.Item.Label>
  </Heading>
);

const HeadingIcon = ({ element, name, ...props }: { element: string; name?: string } & Record<string, any>) => {
  const label = useElementLabel({ type: element, children: [] });

  return (
    <MenuIconItem
      icon={getElementIcon({ type: element } as TElement)}
      name={name ?? label}
      {...props}
    />
  );
};

const BannerHeading = styled(HeadingIcon)`
  svg {
    color: ${(props) => getBannerColour(props.banner, props.theme as any)};
  }
`;

const DropdownIcon = ({ children, label = "Open menu", ref, ...props }: ButtonProps) => (
  <Button
    icon
    ref={ref}
    style={{ minWidth: "8rem", padding: "0.25rem 0.5rem" }}
    label={label}
    {...props}
  >
    {children}
    <Icon name="expand" />
  </Button>
);

const isActive = (type: string) => (editor) => !!editor?.selection && someNode(editor, { match: { type } });

const useShiftKey = () => {
  const [shiftKey, setShiftKey] = useState(false);

  useKey("Shift", () => setShiftKey(true), { event: "keydown" });
  useKey("Shift", () => setShiftKey(false), { event: "keyup" });

  return shiftKey;
};

const convertBanner = (banner: Banner) => (editor: PlateEditor) => {
  const node = getSelectedNode(editor as BaseEditor);
  if (node) {
    setNodes(editor, { type: ELEMENT_BANNER, banner }, { at: node[1] });
  }
};

const isActiveBanner = (banner: Banner) => (editor: PlateEditor) => {
  return !!editor?.selection && someNode(editor, { match: (e) => e.type === ELEMENT_BANNER && e.banner === banner });
};

const restoreEditorSelection = (editor: PlateEditor) => {
  const selection =
    editor.selection ?? (editor as PlateEditor & { prevSelection?: PlateEditor["selection"] }).prevSelection;
  if (selection) {
    focusEditor(editor, selection);
  }

  return selection;
};

export const TurnIntoDropdownMenu = () => {
  const source = usePlateEditorState(EditorState.source);
  const sourceRef = usePlateEditorRef(EditorState.source);
  const [sourceNode] = getSelectedNode(source as BaseEditor);
  const toolbarElement = useOptionalMultiEditor()?.toolbarState[EditorState.source]?.currentElement;
  const { t } = useTranslation();
  const { scheduleFocus, onCloseAutoFocus } = useDeferredEditorFocus(sourceRef);

  const triggerTurnInto = (activeType: string, inactiveType?: string, customToggle?: (editor: PlateEditor) => void) => {
    if (!sourceRef) {
      return;
    }
    const selection = restoreEditorSelection(sourceRef);

    if (customToggle) {
      customToggle(sourceRef);
    } else {
      toggleNodeType(sourceRef, { activeType, inactiveType });
    }

    scheduleFocus(sourceRef.selection ?? selection);
  };

  return (
    <DropdownMenu>
      <DropdownMenu.Trigger asChild>
        <Button
          variant="ghost"
          size="sm"
          label={t("release_centre.turn_into_tooltip")}
          className="text-base px-2 py-1"
        >
          {useElementLabel((toolbarElement as TElement) ?? (sourceNode as TElement) ?? { type: ELEMENT_PARAGRAPH })}
          <Icon name="expand" />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        align="start"
        sideOffset={4}
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <DropdownMenu.Label>{t("release_centre.turn_into_tooltip")}</DropdownMenu.Label>
        <DropdownMenu.Item onSelect={() => triggerTurnInto(ELEMENT_PARAGRAPH, ELEMENT_LIC)}>
          <HeadingIcon element={ELEMENT_PARAGRAPH} />
        </DropdownMenu.Item>
        <DropdownMenu.Item onSelect={() => triggerTurnInto(ELEMENT_H1)}>
          <HeadingIcon element={ELEMENT_H1} />
        </DropdownMenu.Item>
        <DropdownMenu.Item onSelect={() => triggerTurnInto(ELEMENT_H2)}>
          <HeadingIcon element={ELEMENT_H2} />
        </DropdownMenu.Item>
        <DropdownMenu.Item onSelect={() => triggerTurnInto(ELEMENT_H3)}>
          <HeadingIcon element={ELEMENT_H3} />
        </DropdownMenu.Item>
        <DropdownMenu.Item onSelect={() => triggerTurnInto(ELEMENT_H4)}>
          <HeadingIcon element={ELEMENT_H4} />
        </DropdownMenu.Item>
        <DropdownMenu.Item onSelect={() => triggerTurnInto(ELEMENT_H5)}>
          <HeadingIcon element={ELEMENT_H5} />
        </DropdownMenu.Item>
        <DropdownMenu.Item onSelect={() => triggerTurnInto(ELEMENT_H6)}>
          <HeadingIcon element={ELEMENT_H6} />
        </DropdownMenu.Item>
        {Object.values(Banner).map((banner) => (
          <DropdownMenu.Item
            key={`btn-${banner}`}
            onSelect={() => triggerTurnInto(ELEMENT_BANNER, undefined, convertBanner(banner))}
          >
            <BannerHeading
              banner={banner}
              element={ELEMENT_BANNER}
              name={t(`documents.${banner}_banner_label`)}
            />
          </DropdownMenu.Item>
        ))}
      </DropdownMenu.Content>
    </DropdownMenu>
  );
};

export const MoreDropdownMenu = () => {
  const [, setClipboard] = useCopyToClipboard();
  const notify = useNotification();
  const [debug, setDebug] = useLocalStorage("rule-author-debug", false);
  const openMetadataModal = useDocumentMetadataModal();
  const { document } = useRuleDocument();
  const { highlightConnectedTo } = useRelease();
  const { t } = useTranslation();
  const rulesEditorRef = usePlateEditorRef(EditorState.rules);

  const [highlightConnectedToRootOpen, setHighlightConnectedToRootOpen] = useState(false);

  return (
    <>
      <DropdownMenu>
        <DropdownMenu.Trigger asChild>
          <Button
            icon
            label="More settings"
          >
            <Icon name="show-more" />
          </Button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content
          align="end"
          sideOffset={4}
        >
          <DropdownMenu.Item
            onSelect={() => {
              if (document.document?.id) {
                openMetadataModal(document.document.id);
              }
            }}
          >
            <MenuIconItem
              icon={<Icon name="metadata" />}
              name={t("documents.metadata")}
            />
          </DropdownMenu.Item>
          <DropdownMenu.Item
            onSelect={() => {
              setHighlightConnectedToRootOpen(true);
            }}
          >
            <MenuIconItem
              icon={<Icon name="waypoints" />}
              name={t("documents.debug_connected_to")}
            />
          </DropdownMenu.Item>
          <FeatureGuard feature="developer.debug">
            <DropdownMenu.Separator />
            <DropdownMenu.Item
              onSelect={() => {
                if (rulesEditorRef) {
                  setClipboard(JSON.stringify(rulesEditorRef.children, null, 2));
                  notify(t("documents.rules_copied_message"), "info", 1.5);
                }
              }}
            >
              <MenuIconItem
                icon={<Icon name="copy" />}
                name={t("documents.debug_rules")}
              />
            </DropdownMenu.Item>
            <DropdownMenu.Item
              onSelect={() => {
                setDebug(!debug);
              }}
            >
              <MenuIconItem
                icon={<Icon name="bug" />}
                name={t("documents.debug_mode")}
              />
            </DropdownMenu.Item>
          </FeatureGuard>
        </DropdownMenu.Content>
      </DropdownMenu>
      <HighlightConnectedToRootModal
        open={highlightConnectedToRootOpen}
        onClose={() => {
          setHighlightConnectedToRootOpen(false);
        }}
      />
    </>
  );
};

export const FontToolbar = ({ editorId }: { editorId?: string } = {}) => {
  const { t } = useTranslation();
  return (
    <>
      <MarkToolbarButton
        id={editorId}
        icon={<Icon name="bold" />}
        type={MARK_BOLD}
        tooltip={t("documents.bold_tooltip")}
        hotkeys={["mod", "b"]}
      />
      <MarkToolbarButton
        id={editorId}
        icon={<Icon name="italic" />}
        type={MARK_ITALIC}
        tooltip={t("documents.italic_tooltip")}
        hotkeys={["mod", "i"]}
      />
      <MarkToolbarButton
        id={editorId}
        icon={<Icon name="underline" />}
        type={MARK_UNDERLINE}
        tooltip={t("documents.underline_tooltip")}
        hotkeys={["mod", "u"]}
      />
      <MarkToolbarButton
        id={editorId}
        icon={<Icon name="strikethrough" />}
        type={MARK_STRIKETHROUGH}
        tooltip={t("documents.strikethrough_tooltip")}
        hotkeys={["mod", "shift", "x"]}
      />
    </>
  );
};

export const AddSectionToolbarButton = () => {
  const { t } = useTranslation();
  return (
    <EditorToolbarButton
      id={EditorState.source}
      icon={<Icon name="add-group" />}
      type={ELEMENT_SECTION}
      tooltip={t("documents.section_tooltip")}
      hotkeys={["mod", "alt", "s"]}
      active={isActive(ELEMENT_SECTION)}
      onClick={(editor) => {
        createSectionFromNodes(editor);
      }}
    />
  );
};

export const AutogenRulesToolbarButton = () => {
  const { document, id: documentId } = useRuleDocument();
  const openSuggestRulesModal = useOpenSuggestRulesModal();
  const { getSection } = useSections(documentId);
  const { t } = useTranslation();
  const sourceEditor = usePlateEditorRef(EditorState.source);
  const release = useCurrentRelease();
  return (
    <EditorToolbarButton
      id={EditorState.rules}
      icon={<Icon name="auto-generate-pen" />}
      tooltip={t("documents.autogen_rules_tooltip")}
      onClick={(rulesEditor) => {
        const sections = sourceEditor.children.reduce((acc, element) => {
          if (element.type === ELEMENT_SECTION) {
            const section = getSection((element as any).id);
            if (section) {
              acc.push({ ...section, source: element });
            }
            return acc;
          }
          return acc;
        }, [] as SuggestSection[]);

        const onInsert = (section: SuggestSection, rules: TElement[]) => {
          const existingSection = rulesEditor.children.find((e) => e.id === section.id);
          if (existingSection) {
            const path = findNodePath(rulesEditor, existingSection);
            if (path) {
              replaceNodeChildren(rulesEditor, { at: path, nodes: rules });
            }
          } else {
            insertNodes(rulesEditor, createSection(section.id, rules), {
              at: [rulesEditor.children.length],
            });
          }
        };
        openSuggestRulesModal({
          releaseId: release.id,
          model: release.model,
          sections: sections,
          onInsert,
          // releaseId: document.document.release as string,
        });
      }}
    />
  );
};

/** WARNING - this toolbar button only works in the full RuleAuthor context */
export const AutogenSectionToolbarButton = () => {
  const { document } = useRuleDocument();
  const rulesEditor = usePlateEditorRef(EditorState.rules);
  const openAutoSectionModal = useAutoSectionModal();
  const { t } = useTranslation();

  //console.log("document", document);

  return (
    <EditorToolbarButton
      id={EditorState.source}
      icon={<Icon name="auto-generate-pen" />}
      tooltip={t("documents.auto_section_tooltip")}
      onClick={(editor) => {
        const onInsert = (content: TElement | TElement[], sections: SectionData[]) => {
          replaceNodeChildren(editor, { at: [], nodes: content });
          // we also need to add the sections into rules
          const ruleSections = sections.map((s) => createSection(s.id));
          replaceNodeChildren(rulesEditor, { at: [], nodes: ruleSections });
        };
        openAutoSectionModal({
          source: editor.children,
          onInsert,
          documentId: document.document?.id,
        });
      }}
    />
  );
};

// Toggle ordered list with a specific listStyleType
const toggleOrderedListWithStyle = (listStyleType?: string) => (editor: PlateEditor) => {
  // If no selection, try to focus the editor first or use the last known selection
  if (!editor.selection) {
    // Try to restore selection to the last paragraph or create one
    if (editor.children.length > 0) {
      const lastNode = editor.children[editor.children.length - 1];
      const lastPath = [editor.children.length - 1];
      if (lastNode && isElement(lastNode)) {
        // Set selection to the end of the last node
        const endOffset = Node.string(lastNode).length;
        Transforms.select(editor as BaseEditor, {
          anchor: { path: lastPath, offset: endOffset },
          focus: { path: lastPath, offset: endOffset },
        });
      }
    }

    // If still no selection, can't proceed
    if (!editor.selection) {
      console.log("[toggleOrderedListWithStyle] Still no selection after attempting to restore");
      return;
    }
  }

  // Check if we're already in an ordered list
  const parentEntry = getParentNode(editor, editor.selection);
  let isAlreadyInList = false;
  let listPath: Path | undefined;

  if (parentEntry) {
    const [parentNode, parentPath] = parentEntry;
    if (parentNode.type === ELEMENT_LIC) {
      // We're in a list item, check if parent is an ordered list
      const listEntry = getParentNode(editor, parentPath);
      if (listEntry) {
        const [listNode, listNodePath] = listEntry;
        if (listNode.type === ELEMENT_OL) {
          isAlreadyInList = true;
          listPath = listNodePath;
        }
      }
    } else if (parentNode.type === ELEMENT_OL) {
      // Already at the list level
      isAlreadyInList = true;
      listPath = parentPath;
    }
  }

  if (isAlreadyInList && listPath) {
    // Already in a list, just update the listStyleType
    const currentNode = getNode(editor, listPath);
    withoutNormalizing(editor, () => {
      setNodes(editor, { listStyleType }, { at: listPath });
    });
    const updatedNode = getNode(editor, listPath);
    return;
  }

  // Not in a list, toggle to create one
  format(editor, () => {
    withoutNormalizing(editor, () => {
      // Store the selection anchor before toggling
      const selectionBefore = editor.selection;

      // First, toggle the list
      toggleList(editor, {
        type: ELEMENT_OL,
      });

      // Find the list node that contains the selection
      // Try multiple approaches to find it
      if (listStyleType && editor.selection) {
        let targetListPath: Path | undefined;

        // Approach 1: Get parent node from current selection
        const parentEntry = getParentNode(editor, editor.selection);
        if (parentEntry) {
          const [parentNode, parentPath] = parentEntry;
          if (parentNode.type === ELEMENT_LIC) {
            // We're in a list item, get its parent (the list)
            const listEntry = getParentNode(editor, parentPath);
            if (listEntry) {
              const [listNode, listPath] = listEntry;
              if (listNode.type === ELEMENT_OL) {
                targetListPath = listPath;
              }
            }
          } else if (parentNode.type === ELEMENT_OL) {
            // Already at the list level
            targetListPath = parentPath;
          }
        }

        // Approach 2: Use Editor.above to find the list node
        if (!targetListPath && editor.selection) {
          const listEntry = Editor.above(editor as BaseEditor, {
            at: editor.selection,
            match: (n) => (n as TElement).type === ELEMENT_OL,
          });
          if (listEntry) {
            const [listNode, listPath] = listEntry;
            if ((listNode as TElement).type === ELEMENT_OL) {
              targetListPath = listPath;
            }
          }
        }

        // Approach 3: Fallback - search through editor children for the most recent OL
        if (!targetListPath) {
          console.warn(
            "[toggleOrderedListWithStyle] Could not find list node. Selection:",
            editor.selection,
            "Editor children:",
            editor.children.length,
          );
          // Look for the last OL node in the editor (most likely the one just created)
          for (let i = editor.children.length - 1; i >= 0; i--) {
            const node = editor.children[i];
            if (node && (node as TElement).type === ELEMENT_OL) {
              targetListPath = [i];
              break;
            }
          }
        }

        if (targetListPath) {
          setNodes(editor, { listStyleType }, { at: targetListPath });
        } else {
          console.error("[toggleOrderedListWithStyle] Failed to find list node after toggling");
        }
      }
    });
  });
};

// Check if current selection is in an ordered list with a specific listStyleType
const isActiveOrderedListWithStyle = (listStyleType: string) => (editor: PlateEditor) => {
  if (!editor?.selection) {
    return false;
  }
  return someNode(editor, {
    match: (node) => {
      return (
        node.type === ELEMENT_OL && (node as TElement & { listStyleType?: string }).listStyleType === listStyleType
      );
    },
  });
};

const LIST_TYPES = [
  { value: "decimal", label: "1, 2, 3" },
  { value: "lower-alpha", label: "a, b, c" },
  { value: "upper-alpha", label: "A, B, C" },
  { value: "lower-roman", label: "i, ii, iii" },
  { value: "upper-roman", label: "I, II, III" },
];

const applyListStyle = (editor: PlateEditor, listType: { value: string; label: string }) => {
  const isInList = !!editor?.selection && someNode(editor, { match: { type: ELEMENT_OL } });

  if (isInList) {
    let listPath: Path | undefined;
    if (!editor.selection) return;
    const parentEntry = getParentNode(editor, editor.selection);
    if (parentEntry) {
      const [parentNode, parentPath] = parentEntry;
      if (parentNode.type === ELEMENT_LIC) {
        const listEntry = getParentNode(editor, parentPath);
        if (listEntry) {
          const [listNode, listNodePath] = listEntry;
          if (listNode.type === ELEMENT_OL) {
            listPath = listNodePath;
          }
        }
      } else if (parentNode.type === ELEMENT_OL) {
        listPath = parentPath;
      }
    }
    if (!listPath && editor.selection) {
      const listEntry = Editor.above(editor as BaseEditor, {
        at: editor.selection,
        match: (n) => (n as TElement).type === ELEMENT_OL,
      });
      if (listEntry) {
        const [, foundPath] = listEntry;
        listPath = foundPath;
      }
    }
    if (listPath) {
      withoutNormalizing(editor, () => {
        setNodes(editor, { listStyleType: listType.value }, { at: listPath });
      });
    } else {
      console.warn("[ListTypeDropdown] Could not find list path to update");
    }
  } else {
    toggleOrderedListWithStyle(listType.value)(editor);
  }
};

const ListTypeDropdown = () => {
  const editorRef = usePlateEditorRef(EditorState.source);

  const control = (
    <Button
      icon
      // style={{ minWidth: "auto", padding: "0.25rem 0.25rem" }}
      label="List style"
    >
      <Icon name="expand" />
    </Button>
  );

  return (
    <DropdownMenu>
      <DropdownMenu.Trigger asChild>{control}</DropdownMenu.Trigger>
      <DropdownMenu.Content
        align="start"
        sideOffset={4}
      >
        <DropdownMenu.Label>List Style</DropdownMenu.Label>
        {LIST_TYPES.map((listType) => (
          <DropdownMenu.Item
            key={listType.value}
            onSelect={() => {
              if (editorRef) {
                applyListStyle(editorRef, listType);
              }
            }}
          >
            <MenuIconItem
              icon={<span style={{ fontFamily: "monospace", fontSize: "0.875rem" }}>{listType.label}</span>}
              name={listType.value}
            />
          </DropdownMenu.Item>
        ))}
      </DropdownMenu.Content>
    </DropdownMenu>
  );
};

export const ListToolbarButtons = ({ editorId }: { editorId?: string } = {}) => {
  const { t } = useTranslation();
  const { active: bulletListActive } = useToolbarListState(editorId, ELEMENT_UL);
  return (
    <ButtonGroup>
      <BlockToolbarButton
        id={editorId}
        icon={<Icon name="list" />}
        type={ELEMENT_UL}
        active={bulletListActive}
        tooltip={t("documents.bullet_tooltip")}
      />
      <ListTypeDropdown />
    </ButtonGroup>
  );
};

export const ActiveUsers = ({ documentId }: { documentId: string }) => {
  const editors = useDocumentEditors(documentId);

  if (editors.length === 0) return null;

  return (
    // @ts-ignore TODO fix the typing
    <AvatarGroup size="medium">
      {editors?.map((e) => (
        <Tooltip
          key={e.identity_id}
          title={e.full_name}
          placement="bottom"
          // PopperProps={AvatarPopperProps}
        >
          <Avatar
            alt={e.full_name}
            name={e.full_name}
            size="sm"
          />
        </Tooltip>
      ))}
    </AvatarGroup>
  );
};

export const SourceToolbar = ({
  hideSection,
  hideAutogen,
  editorId,
}: { hideSection?: boolean; hideAutogen?: boolean; editorId?: string }) => {
  // alex - this is a hack because the autogen section stuff broke task management
  const context = useContext(RuleDocumentContext);
  const optional = [
    hideSection ? null : <AddSectionToolbarButton key="section" />,
    hideAutogen || !context ? null : (
      <FeatureGuard
        key="autosection"
        feature="experimental.ai_chat"
      >
        <AutogenSectionToolbarButton />
      </FeatureGuard>
    ),
  ].filter(Boolean);
  return (
    <>
      <Toolbar.Group>
        <TurnIntoDropdownMenu />
        <FontToolbar editorId={editorId} />
      </Toolbar.Group>
      <Toolbar.Divider />
      <Toolbar.Group>
        <ListToolbarButtons editorId={editorId} />
        <LinkToolbarButton icon={<Icon name="public-link" />} />
      </Toolbar.Group>
      {optional.length === 0 ? null : (
        <>
          <Toolbar.Divider />
          {optional}
        </>
      )}
    </>
  );
};

const turnIntoConclusion = (editor: PlateEditor) => {
  const [node, path] = getSelectedNode(editor);
  if (node && path) {
    if (node.expression === ELEMENT_CONDITION) {
      setNodes(editor, { expression: ELEMENT_CONCLUSION }, { at: path });
    } else {
      paragraphToRule(editor, path, ELEMENT_CONCLUSION);
    }
  }
};

const turnIntoCondition = (editor: PlateEditor) => {
  const [node, path] = getSelectedNode(editor);
  if (node && path) {
    if (node.type === ELEMENT_PARAGRAPH) {
      paragraphToRule(editor, path, ELEMENT_CONDITION);
    } else if (node.expression === ELEMENT_CONCLUSION) {
      setNodes(editor, { expression: ELEMENT_CONDITION }, { at: path });
    }
  }
};

const turnIntoParagraph = (editor: PlateEditor) => {
  const [node, path] = getSelectedNode(editor);
  if (node && path) {
    if (node.type === ELEMENT_PARAGRAPH) {
      return;
    }
    if (isRule(editor, path)) {
      ruleToParagraph(editor, path);
      return;
    }
    const text = Array.from(getNodeTexts(node))
      .map(([n]) => n.text)
      .filter((t) => t.trim().length > 0)
      .join(" ");
    withoutNormalizing(editor, () => {
      removeNodes(editor, { at: path });
      insertNodes(editor, createParagraph(text), { at: path, select: true });
    });
  }
};

export const RulesToolbar = ({
  editorId = EditorState.rules,
  children,
}: { editorId?: string; children?: ReactNode }) => {
  const rules = usePlateEditorState(editorId);
  const rulesRef = usePlateEditorRef(editorId);
  const { t } = useTranslation();
  const toolbarElement = useOptionalMultiEditor()?.toolbarState[editorId]?.currentElement;

  const [ruleNode] = getSelectedNode(rules as BaseEditor);
  const disableTurnInto =
    rules.selection &&
    ruleNode &&
    (isTableHeaderRow(rules, rules.selection.anchor.path) ||
      isLastTableRow(rules, rules.selection.anchor.path) ||
      ![ELEMENT_RULE, ELEMENT_PARAGRAPH].includes((ruleNode as TElement).type));

  const level = getRuleLevel(rules, rules.selection?.anchor.path ?? []);
  const { scheduleFocus, onCloseAutoFocus } = useDeferredEditorFocus(rulesRef);
  const triggerRulesTurnInto = (handler: (editor: PlateEditor) => void) => {
    if (!rulesRef) {
      return;
    }

    const selection = restoreEditorSelection(rulesRef);
    handler(rulesRef);
    scheduleFocus(rulesRef.selection ?? selection);
  };

  return (
    <>
      <Toolbar.Group>
        <DropdownMenu>
          <DropdownMenu.Trigger asChild>
            <Button
              variant="ghost"
              size="sm"
              label={t("release_centre.turn_into_tooltip")}
              className={cn("text-base px-2 py-1", disableTurnInto && "hidden")}
            >
              {useElementLabel((toolbarElement as TElement) ?? (ruleNode as TElement) ?? { type: ELEMENT_PARAGRAPH })}
              <Icon name="expand" />
            </Button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content
            align="start"
            sideOffset={4}
            onCloseAutoFocus={onCloseAutoFocus}
          >
            <DropdownMenu.Item onSelect={() => triggerRulesTurnInto(turnIntoConclusion)}>
              <MenuIconItem
                icon={<Icon name="outcome" />}
                name={t("documents.conclusion_label")}
              />
            </DropdownMenu.Item>
            <DropdownMenu.Item onSelect={() => triggerRulesTurnInto(turnIntoCondition)}>
              <MenuIconItem
                icon={<Icon name="add-rule" />}
                name={t("documents.condition_label")}
              />
            </DropdownMenu.Item>
            <DropdownMenu.Item onSelect={() => triggerRulesTurnInto(turnIntoParagraph)}>
              <MenuIconItem
                icon={<Icon name="non-rule" />}
                name={t("documents.paragraph_label")}
              />
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu>
        <EditorToolbarButton
          id={EditorState.rules}
          icon={<Icon name="add-rule" />}
          type={ELEMENT_OPERATOR}
          tooltip={t("documents.operator_tooltip")}
          hotkeys={["mod", "o"]}
          // disable if no selection, or selected node is paragraph
          disable={(editor) => !editor.selection || level < 1 || getSelectedNode(editor)[0]?.type === ELEMENT_PARAGRAPH}
          onClick={(editor) => {
            insertOperator(editor);
          }}
        />
        <EditorToolbarButton
          id={EditorState.rules}
          icon={<Icon name="list-indent-increase" />}
          type={ELEMENT_CONNECTOR}
          tooltip={t("documents.indent_tooltip")}
          hotkeys={["mod", "g"]}
          // disable if no selection, or selected node is paragraph
          disable={(editor) => !editor.selection || level < 1 || getSelectedNode(editor)[0]?.type === ELEMENT_PARAGRAPH}
          onClick={(editor) => {
            const range = editor.selection;
            if (range) {
              insertConnector(editor, range);
            }
          }}
        />
        <EditorToolbarButton
          id={EditorState.rules}
          icon={<Icon name="keyboard-return" />}
          type={ELEMENT_HR}
          tooltip={t("documents.rule_break_tooltip")}
          hotkeys={["mod", "shift", "enter"]}
          disable={(editor) => !editor.selection || level < 2}
          onClick={(editor) => editor.selection && insertRuleBreak(editor, Path.parent(editor.selection.anchor.path))}
        />
        {/* <BlockToolbarButton icon={<FormatListBulleted />} type={ELEMENT_UL} tooltip='Bullet list' /> */}
        {/* TODO actually implement tables */}
        {/* <BlockToolbarButton icon={<Icon name="add-table" />} type={ELEMENT_TABLE} tooltip='Insert Decision Table' disable={true} /> */}
        <TablesToolbar />
        {children}
      </Toolbar.Group>
    </>
  );
};
